import { cx } from '@emotion/css';
import { useRefreshTranslations } from '../../domain/i18n/use-refresh-translations';
import { getMetaCspHeader } from '../../domain/security/csp';
import { CspDirectives } from '../../domain/security/csp.model';
import {
  AssetsStaticProps,
  FooterStaticProps,
  HeaderStaticProps,
  NavigationStaticProps,
  PageStaticProps,
} from '../../domain/static-content/static-content.static-generation.model';
import { FeedbackContextProvider } from '../../ui/feedback/use-feedback';
import { Footer } from '../../ui/footer/footer';
import { Navigation } from '../../ui/navigation/navigation';
import { newrelic } from '../../ui/newrelic/newrelic';
import { UnsupportedBrowser } from '../../ui/unsupported-browser/unsupported-browser';
import { CookieConsent } from '../../ui/cookie-consent/cookie-consent';
import { getLocale } from '../../util/locales/locales';
import { AxiosError } from 'axios';
import { Locale } from 'date-fns';
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import Head from 'next/head';
import React, { FC, useEffect, useState } from 'react';
import { Toaster } from '../toaster/toaster';
import { SWRConfig } from 'swr';
import { motion } from 'framer-motion';

import { Backdrop } from '../backdrop/backdrop';
import { HeaderFooterBackdropContext } from '../backdrop/use-header-footer-backdrop';
import { CoreContextProvider } from '../context/core.context';
import { useInitCore } from '../context/use-init-core';
import { HeaderWrapper } from '../header/header-wrapper';
import styles from './scaffold.module.scss';
import { useJsEnabled } from './use-js-enabled';
import { useStaticContent } from './use-static-content';
import { useQueryClient } from '@tanstack/react-query';
import { useRefreshAssets } from '../assets/use-refresh-assets';
import { STATIC_ASSETS_CACHE_KEY } from '../../util/assets/assets-key';
import { useIsStickyMode } from './use-is-sticky-mode';
import { useLDUserContext } from './use-LD-user-context';
import { SharedFeatureFlagsContextProvider } from '../shared-feature-flags/shared-feature-flags.context';
import { LoadingOverlayContextProvider } from './../overlay/loading-overlay.context';
import { DSSkipToContent } from '@stihl-design-system/components';
import { useTranslation } from 'next-i18next';

export interface ScaffoldProps extends HeaderStaticProps, FooterStaticProps, NavigationStaticProps, AssetsStaticProps, PageStaticProps {
  cspDirectives?: CspDirectives;
  jsSupportCheckEnabled?: boolean;
  onPostLogin?: () => void;
  onLDContextReady?: () => void;
  professionalPortalEnabled?: boolean;
  shouldHideCookieConsent?: boolean;
  skipToMainContentTargetElementId?: string;
}

if (typeof window !== 'undefined') {
  if (process.env.NEXT_PUBLIC_FULLSTORY_ORG_ID) {
    import('@fullstory/browser').then((FullStory) =>
      FullStory.init({
        orgId: process.env.NEXT_PUBLIC_FULLSTORY_ORG_ID!,
        devMode: process.env.NODE_ENV === 'development',
      })
    );
  }
}

const ScaffoldComponent: FC<React.PropsWithChildren<ScaffoldProps>> = ({
  assets = {
    items: [],
    tags: [],
  },
  children,
  cspDirectives,
  footer = {
    legalEntity: '',
    sections: [],
    hide: false,
    minimal: false,
  },
  header = {
    hasSecurity: true,
    profileMenuLinkScope: 'global_stihlIdProfile',
    hide: false,
    userInfoDataProvider: 'profile',
  },
  navigation = {
    hide: false,
    preventRender: false,
    navItems: [],
  },
  jsSupportCheckEnabled = true,
  onPostLogin,
  onLDContextReady,
  professionalPortalEnabled = false,
  shouldHideCookieConsent = false,
  skipToMainContentTargetElementId,
  page,
}) => {
  const [showFooterHeaderBackdrop, setShowFooterHeaderBackdrop] = useState(false);
  const [showContentBackdrop, setShowContentBackdrop] = useState(false);
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const requiresAuthentication = header.hasSecurity;

  useEffect(() => {
    if (assets?.items?.length) {
      queryClient.setQueryData([STATIC_ASSETS_CACHE_KEY], assets.items);
    }
  }, [JSON.stringify(assets?.items)]);

  useJsEnabled();
  useRefreshTranslations();
  useRefreshAssets(assets?.tags);
  const coreContext = useInitCore({
    requiresAuthentication,
    displayLoggedInUser: !header.hide,
    userInfoDataProvider: header.userInfoDataProvider || 'profile',
    onPostLogin,
  });

  const { navItems, sections, profileMenuItems } = useStaticContent({ footer, header, navigation });

  useEffect(() => {
    const anonymousId = coreContext.auth.user?.anonymousId;
    if (anonymousId) {
      if (newrelic) {
        newrelic.setCustomAttribute('anonymousId', anonymousId);
      }
      if (process.env.NEXT_PUBLIC_FULLSTORY_ORG_ID && (window as any).FS) {
        (window as any).FS.identify(anonymousId);
      }
    }
  }, [coreContext.auth.user?.anonymousId]);

  useEffect(() => {
    const importLocaleFile = async () => {
      let locale: Locale;
      try {
        locale = await (
          await import(
            /* webpackMode: "lazy", webpackChunkName: "df-[index]", webpackExclude: /_lib/ */
            `date-fns/locale/${getLocale().RFC5646}/index.js`
          )
        ).default;
      } catch (error) {
        const currentLanguage = getLocale().language;
        locale = await (
          await import(
            /* webpackMode: "lazy", webpackChunkName: "df-[index]", webpackExclude: /_lib/ */
            `date-fns/locale/${currentLanguage === 'en' ? 'en-GB' : currentLanguage}/index.js`
          )
        ).default;
      }

      (window as any).dateFnsLocale = locale;
    };

    importLocaleFile();
  }, []);

  useLDUserContext(coreContext.auth.user, onLDContextReady);

  // navigation scroll logic
  const { isStickyMode, navigationVariants, navigationTransition } = useIsStickyMode(header.hide);

  return (
    <>
      <CoreContextProvider coreContext={coreContext}>
        <SharedFeatureFlagsContextProvider requiresAuthentication={requiresAuthentication}>
          <LoadingOverlayContextProvider>
            <HeaderFooterBackdropContext.Provider
              value={{
                visible: showFooterHeaderBackdrop,
                setVisible: setShowFooterHeaderBackdrop,
              }}
            >
              <FeedbackContextProvider reverseFeedbackBanner={professionalPortalEnabled}>
                <Backdrop visible={showFooterHeaderBackdrop || showContentBackdrop} className={'sticky top-0 z-[32]'}>
                  <HeaderWrapper
                    hide={!!header.hide}
                    profileMenuItems={profileMenuItems}
                    requiresAuthentication={header.hasSecurity}
                    className={cx(jsSupportCheckEnabled && styles.noJs)}
                    customHeader={header.customHeader}
                    bannerHeader={header.bannerHeader}
                  />
                </Backdrop>
                <motion.div
                  variants={navigationVariants}
                  transition={navigationTransition}
                  animate={isStickyMode ? 'scrollUp' : 'scrollDown'}
                  className={cx('sticky top-0 z-[31]')}
                >
                  <Backdrop visible={showFooterHeaderBackdrop}>
                    {navigation.hide || navigation?.preventRender ? null : (
                      <div className={cx(jsSupportCheckEnabled && styles.noJs)}>
                        <Navigation navItems={navItems} showBackdrop={setShowContentBackdrop} />
                      </div>
                    )}
                  </Backdrop>
                </motion.div>
                <Head key="scaffold-head">
                  <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
                  <meta httpEquiv="Content-Security-Policy" content={getMetaCspHeader(cspDirectives)} />
                </Head>
                {skipToMainContentTargetElementId && (
                  <DSSkipToContent targetElementId={skipToMainContentTargetElementId}>
                    {t('shared:common_skip_to_main_content') ?? 'Skip To Main Content'}
                  </DSSkipToContent>
                )}
                <div className={cx('app', jsSupportCheckEnabled && styles.noJs)}>
                  <Toaster isSplitview={professionalPortalEnabled} />
                  <SWRConfig
                    value={{
                      revalidateOnFocus: false,
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      onErrorRetry: (error: AxiosError<any, any>, key, config, revalidate, { retryCount }) => {
                        if (error && error?.response?.data.error === 'invalid_grant') {
                          coreContext.auth.onLogout();
                        } else if (error.message === 'No refresh token found') {
                          setTimeout(() => revalidate({ retryCount }), 300);
                        }
                      },
                    }}
                  >
                    <Backdrop visible={showContentBackdrop} className={cx('z-10 flex flex-grow flex-col', page?.backdropClassName)}>
                      {children}
                    </Backdrop>
                  </SWRConfig>
                  {!footer.hide && (sections?.length || footer?.legalEntity) && (
                    <Backdrop visible={showFooterHeaderBackdrop || showContentBackdrop}>
                      <Footer legalEntity={footer.legalEntity || ''} sectionLinks={sections} minimal={!!footer.minimal}></Footer>
                    </Backdrop>
                  )}
                </div>
                {!shouldHideCookieConsent && <CookieConsent />}
              </FeedbackContextProvider>
            </HeaderFooterBackdropContext.Provider>
          </LoadingOverlayContextProvider>
        </SharedFeatureFlagsContextProvider>
      </CoreContextProvider>
      <noscript>
        <UnsupportedBrowser assets={assets?.items} />
      </noscript>
    </>
  );
};

const launchDarklyClientSideId = process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_SIDE_ID;

export const Scaffold = launchDarklyClientSideId
  ? (withLDProvider({
      clientSideID: launchDarklyClientSideId,
      context: {
        kind: 'multi',
        user: {
          key: 'anonymous',
        },
      },
      reactOptions: {
        useCamelCaseFlagKeys: false,
      },
    })(ScaffoldComponent) as FC<React.PropsWithChildren<ScaffoldProps>>)
  : ScaffoldComponent;
